<template>
  <div>
    <v-btn
      :disabled="isButtonDisabled()"
      color="primary--text"
      @click="openDialog"
    >
      <v-icon left>save</v-icon
      ><span v-if="!automatedAnalysisMode"
        >{{ operation.toUpperCase() }} STUDY DATASET</span
      ><span v-else>BEGIN ANALYSIS</span>
    </v-btn>
    <!-- SAVE COLLECTION FORM DIALOG -->
    <v-dialog v-model="dialog" width="500">
      <v-card>
        <v-card-title color="white" primary-title>
          <v-icon color="primary">save</v-icon>
          <span class="primary--text text--darken-3 title pl-2"
            >{{ operation }} Study Dataset</span
          >
        </v-card-title>

        <v-card-text class="primary primary--text text--lighten-5 pt-4">
          <v-form
            v-if="collection == null"
            ref="form"
            v-model="valid"
            class="white"
            @submit.prevent="onSaveCollection"
          >
            <v-text-field
              ref="vtf"
              v-model="collectionName"
              :rules="[
                () => !!collectionName || 'Study Dataset name is required.',
              ]"
              prepend-inner-icon="table_chart"
              label="Please name your study dataset."
              filled
              text
              background-color="grey lighten-4"
              class="mt-2"
            >
            </v-text-field>
          </v-form>
          <div v-else>
            <p>
              You are about to update the Study Dataset by adding and/or
              removing the variable(s) specified below. Please note that this
              action cannot be undone, and may change or invalidate existing
              study groups and/or analyses. Clicking "UPDATE DATASET" will:
            </p>

            <div v-if="varsToDelete.length > 0">
              REMOVE the following variable(s):
              <ul>
                <li v-for="(v, i) in varsToDelete" :key="`vtd-i-${i}`">
                  {{ useLongScaleNames ? v.label : v.abbreviation }}
                </li>
              </ul>
            </div>
            <div v-if="varsToAdd.length > 0">
              ADD the following variable(s):
              <ul>
                <li v-for="(v, i) in varsToAdd" :key="`vta-i-${i}`">
                  {{ useLongScaleNames ? v.scale : v.abbreviation }}
                </li>
              </ul>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="dialog = false">
            <v-icon left>close</v-icon> Cancel
          </v-btn>
          <v-btn :loading="loading" color="primary" @click="saveClicked">
            <v-icon left>save</v-icon> {{ operation }} Dataset</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import { actions } from '@/store/modules/datasetManager/types';

export default {
  props: {
    variables: {
      type: Array,
      default: () => [],
    },
    datasetIds: {
      type: Array,
      default: () => [],
    },
    numSubjectsSelected: {
      type: Number,
      default: 0,
    },
    numObservationVarsSelected: {
      type: Number,
      default: 0,
    },
    numSubjectVarsSelected: {
      type: Number,
      default: 0,
    },
    automatedAnalysisMode: {
      type: Boolean,
      default: false,
    },
    automatedAnalysisPredictorVars: {
      type: Array,
      default: () => [],
    },
    automatedAnalysisOutputVars: {
      type: Array,
      default: () => [],
    },
    collection: {
      type: Object,
      default: null,
    },
    useLongScaleNames: {
      type: Boolean,
      default: false,
    },
    isLongitudinal: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    collectionName: '',
    valid: true,
    dialog: false,
    loading: false,
    varsToDelete: [],
    varsToAdd: [],
  }),
  computed: {
    areVariablesSelected() {
      return this.variables.length > 0;
    },
    // saving new collection or updating existing one?
    operation() {
      return this.collection == null ? 'Save' : 'Update';
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.showing();
        this.$emit('dialogOpen', this.dialog);
      }
    },
  },
  methods: {
    ...mapActions('datasetManager', {
      saveCollection: actions.SAVE_COLLECTION,
    }),
    openDialog() {
      if (this.collection != null) {
        let ad_vars = this.getAddedAndDeletedVars();
        this.varsToDelete = [
          ...ad_vars['subj']['delete'],
          ...ad_vars['obs']['delete'],
        ];
        this.varsToAdd = [...ad_vars['subj']['add'], ...ad_vars['obs']['add']];
      }
      this.dialog = true;
    },
    showing() {
      setTimeout(() => {
        if ('vtf' in this.$refs) {
          this.$refs.vtf.focus();
        }
      });
    },
    _varsToH(vs, acc_fn) {
      let h = {};
      vs.forEach(v => {
        h[acc_fn(v)] = v;
      });
      return h;
    },
    // lists of variables added or deleted in update mode
    getAddedAndDeletedVars() {
      // variables selected now
      let sel_obs_vars = this.variables.filter(v => 'flip_axis' in v);
      let sel_obs_ids = this._varsToH(sel_obs_vars, v => v.id);

      let sel_subj_vars = this.variables.filter(v => !('flip_axis' in v));
      let sel_subj_ids = this._varsToH(sel_subj_vars, v => v.id);

      // variables selected in the original collection
      let subj_vars = this.collection.subject_variables;
      let subj_ids = this._varsToH(subj_vars, v => v.ontology.id);

      let obs_vars = this.collection.observation_variables;
      let obs_ids = this._varsToH(obs_vars, v => v.ontology.id);

      let added_subj_vars = [];
      let added_obs_vars = [];
      let deleted_subj_vars = [];
      let deleted_obs_vars = [];

      // new variables
      Object.keys(sel_subj_ids).forEach(vid => {
        if (!(vid in subj_ids)) {
          added_subj_vars.push(sel_subj_ids[vid]);
        }
      });
      Object.keys(sel_obs_ids).forEach(vid => {
        if (!(vid in obs_ids)) {
          added_obs_vars.push(sel_obs_ids[vid]);
        }
      });

      // removed variables
      Object.keys(subj_ids).forEach(vid => {
        if (!(vid in sel_subj_ids)) {
          let dsv = subj_ids[vid].ontology;
          if (dsv.label != 'Dataset') {
            deleted_subj_vars.push(dsv);
          }
        }
      });
      Object.keys(obs_ids).forEach(vid => {
        if (!(vid in sel_obs_ids)) {
          deleted_obs_vars.push(obs_ids[vid].ontology);
        }
      });

      return {
        subj: { add: added_subj_vars, delete: deleted_subj_vars },
        obs: { add: added_obs_vars, delete: deleted_obs_vars },
      };
    },
    isButtonDisabled() {
      let save_not_ok =
        this.numSubjectsSelected === 0 ||
        this.numObservationVarsSelected === 0 ||
        (this.automatedAnalysisMode &&
          (this.automatedAnalysisPredictorVars.length == 0 ||
            this.automatedAnalysisOutputVars.length == 0));
      return save_not_ok;
    },
    async saveClicked() {
      if (this.collection == null) {
        this.onSaveCollection();
      } else {
        this.onUpdateCollection();
      }
    },
    async navigateToNextPage(query) {
      // longitudinal - go to data summary to set first/last visits
      if (this.isLongitudinal) {
        this.$router.push({ name: 'dataSummary', query: query });
      }
      // cross-sectional - go directly to study group selector
      else {
        this.$router.push({ name: 'cohortManager', query: query });
      }
    },
    async onSaveCollection() {
      const { collectionName, variables, datasetIds } = this;

      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          const newCollection = await this.saveCollection({
            collectionName,
            variables,
            datasetIds,
          });
          this.$emit('collectionSaved');
          this.loading = false;
          var query = { collection: newCollection.id };
          if (this.automatedAnalysisMode) {
            query['aa_predictors'] = this.automatedAnalysisPredictorVars.map(
              v => v.id
            );
            query['aa_outputs'] = this.automatedAnalysisOutputVars.map(
              v => v.id
            );
            // encode predictor prefs
            // e.g., aaRanges=quartiles|22,23||tertiles|17,18 - ranges
            // aaMCS=1|22,23||5|17,18 - min category sizes
            // aaWhichOutcomes=firstVisit|9,10||ROC|11,12
            const d1 = '|';
            const d2 = '||';
            var pph = {};
            var addkey = function(i, k) {
              if (!(k in pph)) {
                pph[k] = [];
              }
              pph[k].push(i);
            };
            this.automatedAnalysisPredictorVars.map(v => {
              if ('aaRanges' in v) {
                addkey(v.id, v.aaRanges);
              } else if ('aaMinCatSize' in v) {
                addkey(v.id, v.aaMinCatSize);
              }
              if ('aaWhichOutcome' in v) {
                addkey(v.id, v.aaWhichOutcome);
              }
            });
            query['aa_ranges'] = ['quartiles', 'tertiles', 'halves']
              .filter(r => r in pph)
              .map(r => {
                return r + d1 + pph[r].join(',');
              })
              .join(d2);
            // changes to this array must be coordinated with SharedVariableTable.vue
            query['aa_mcs'] = [1, 5, 10, 25, 50]
              .filter(r => r in pph)
              .map(r => {
                return r + d1 + pph[r].join(',');
              })
              .join(d2);
            query['aa_which_outcomes'] = [
              'firstVisit',
              'lastVisit',
              'change',
              'ROC',
            ]
              .filter(r => r in pph)
              .map(r => {
                return r + d1 + pph[r].join(',');
              })
              .join(d2);
          }
          this.dialog = false;
          // ensure that once the collection has been saved, navigating back will lead to the add/remove variables step
          await window.history.replaceState(
            {},
            '',
            'build?collection=' + newCollection.id
          );
          query['collection'] = newCollection.id;
          this.navigateToNextPage(query);
        } catch (err) {
          this.loading = false;
        }
      }
    },
    // diff current variable selections with saved / collection, apply add/remove actions as needed
    async onUpdateCollection() {
      this.loading = true;
      for (var i = 0; i < this.varsToDelete.length; ++i) {
        const v = this.varsToDelete[i];
        const which = 'flip_axis' in v ? 'observation' : 'subject';
        await axios.delete(
          `/api/collections/${this.collection.id}/${which}_variables/${v.id}`
        );
      }

      for (i = 0; i < this.varsToAdd.length; ++i) {
        const v = this.varsToAdd[i];
        const which = 'flip_axis' in v ? 'observation' : 'subject';
        await axios.post(
          `/api/collections/${this.collection.id}/${which}_variables/${v.id}`
        );
      }
      this.loading = false;
      this.dialog = false;
      var query = { collection: this.collection.id };
      this.navigateToNextPage(query);
    },
  },
};
</script>

<style lang="scss" scoped></style>
